import React, { useEffect, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import DecoupledEditor from "@ckeditor/ckeditor5-build-decoupled-document";
import "./TextEditor.css";
import { logger } from "../../utils/Logger";
import EditorErrorBoundary from "./EditorErrorBoundary";
import {
  markdownToHtml,
  htmlToMarkdown,
  isMarkdownContent,
} from "../../utils/MarkdownProcessor";

interface ITextEditor {
  content: string;
  handleWikiChange: Function;
  disabled: boolean;
  isMaxSD: boolean;
  customStyle?: object;
  showToolbar?: boolean;
  enableMarkdown?: boolean;
}

function setEditorStyles(
  editor: any,
  disabled: boolean,
  showToolbar: boolean = true,
  customStyle: any = {}
) {
  if (disabled || !showToolbar) {
    // remove the toolbar
    editor.ui?.view.toolbar.element.remove();

    const textEditorContent: HTMLWebViewElement =
      document.querySelector(
        ".ck-blurred.ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline.ck-read-only"
      ) ||
      document.querySelector(
        ".ck-focused.ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline"
      );

    const toolbar = document.querySelector(".ck.ck-toolbar") as HTMLElement;
    if (toolbar) {
      toolbar.remove();
    }

    if (textEditorContent) {
      textEditorContent.style.height = "100%";
      textEditorContent.style.top = "0px";
      textEditorContent.style.wordBreak = "break-word";

      // Apply custom font size if provided
      if (customStyle?.fontSize) {
        // Apply font size with higher specificity to override default styles
        textEditorContent.style.fontSize = customStyle.fontSize + " !important";

        // Use a more targeted approach to apply font size
        const applyFontSize = () => {
          const elements = textEditorContent.querySelectorAll("*");
          elements.forEach((el: Element) => {
            (el as HTMLElement).style.fontSize =
              customStyle.fontSize + " !important";
          });
        };

        // Apply immediately
        applyFontSize();

        // Also apply when content changes (debounced)
        setTimeout(applyFontSize, 100);
      }
    }
  } else {
    // Insert the toolbar before the editable area.
    if (editor?.ui?.view?.toolbar?.element?.style) {
      editor.ui.view.toolbar.element.style.zIndex = "9999";
      editor.ui.view.toolbar.element.style.position = "sticky";
      editor.ui.view.toolbar.element.style.width = "100%";
      editor.ui.view.toolbar.element.style.height = "40px";
      editor.ui.view.toolbar.element.style.top = "0px";
    }

    const textEditor = document.querySelector(".text-editor") as HTMLElement;
    // get the content, the second child
    const textEditorContent: HTMLWebViewElement = textEditor
      ?.children[1] as HTMLWebViewElement;

    if (textEditorContent) {
      textEditorContent.style.position = "absolute";
      textEditorContent.style.height = "calc(100% - 60px)";
      textEditorContent.style.top = "60px";
      textEditorContent.style.zIndex = "99";
      textEditorContent.style.width = "100%";
      textEditorContent.style.wordBreak = "break-word";

      // Apply custom font size if provided
      if (customStyle?.fontSize) {
        // Apply font size with higher specificity to override default styles
        textEditorContent.style.fontSize = customStyle.fontSize + " !important";

        // Use a more targeted approach to apply font size
        const applyFontSize = () => {
          const elements = textEditorContent.querySelectorAll("*");
          elements.forEach((el: Element) => {
            (el as HTMLElement).style.fontSize =
              customStyle.fontSize + " !important";
          });
        };

        // Apply immediately
        applyFontSize();

        // Also apply when content changes (debounced)
        setTimeout(applyFontSize, 100);
      }
    }

    editor?.ui
      ?.getEditableElement()
      ?.parentElement.insertBefore(
        editor?.ui.view.toolbar.element,
        editor?.ui.getEditableElement()
      );
  }
}

export const TextEditor: React.FC<ITextEditor> = (props: ITextEditor) => {
  const {
    content,
    handleWikiChange,
    disabled,
    isMaxSD,
    customStyle,
    showToolbar = true,
    enableMarkdown = true,
  } = props;
  // useRef to track the editor
  const editorRef = React.useRef(null);

  // Add a state to track if the editor should be rendered
  const [shouldRenderEditor, setShouldRenderEditor] = useState(false);

  // Use an effect to delay editor rendering
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRenderEditor(true);
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  // on disabled useffect to run the setEditorStyles function with thsame parameters
  useEffect(() => {
    const editor = editorRef?.current?.editor;
    if (!editor) return;
    setEditorStyles(editor, disabled, showToolbar, customStyle);
  }, [disabled, showToolbar, customStyle]);

  useEffect(() => {
    return () => {
      if (editorRef.current?.editor) {
        try {
          // Check if editor exists and has ui with view
          const editor = editorRef.current.editor;
          if (
            editor &&
            editor.ui &&
            editor.ui.view &&
            editor.ui.view.element &&
            typeof editor.destroy === "function"
          ) {
            editor.destroy().catch((err) => {
              logger.error("CKEditor destroy error:", err);
            });
          }
        } catch (err) {
          logger.error("Error during CKEditor cleanup:", err);
        } finally {
          // Always clear the ref to prevent accessing destroyed editor
          editorRef.current = null;
        }
      }
    };
  }, []);

  // Enhanced editor configuration with markdown support
  const getEditorConfig = () => {
    const baseConfig = {
      link: {
        addTargetToExternalLinks: true,
      },
    };

    if (enableMarkdown) {
      return {
        ...baseConfig,
        toolbar: {
          items: [
            "heading",
            "|",
            "bold",
            "italic",
            "strikethrough",
            "code",
            "|",
            "bulletedList",
            "numberedList",
            "|",
            "blockQuote",
            "codeBlock",
            "horizontalLine",
            "|",
            "link",
            "|",
            "undo",
            "redo",
          ],
        },
        codeBlock: {
          languages: [
            { language: "plaintext", label: "Plain text" },
            { language: "javascript", label: "JavaScript" },
            { language: "typescript", label: "TypeScript" },
            { language: "python", label: "Python" },
            { language: "java", label: "Java" },
            { language: "css", label: "CSS" },
            { language: "html", label: "HTML" },
            { language: "json", label: "JSON" },
            { language: "xml", label: "XML" },
            { language: "sql", label: "SQL" },
          ],
        },
        typing: {
          transformations: {
            include: [
              // Enable smart quotes, arrows, etc., but avoid auto-formatting that might interfere with code
              "quotes",
              "typography",
            ],
          },
        },
      };
    }

    return baseConfig;
  };

  // Process content for markdown support
  const processedContent = React.useMemo(() => {
    if (enableMarkdown && content && isMarkdownContent(content)) {
      return markdownToHtml(content);
    }
    return content;
  }, [content, enableMarkdown]);

  return (
    <EditorErrorBoundary>
      <div
        className="text-editor"
        style={
          customStyle ? customStyle : { maxHeight: isMaxSD ? "90%" : "90%" }
        }
      >
        {shouldRenderEditor ? (
          <CKEditor
            ref={editorRef}
            onReady={(editor) => {
              setEditorStyles(editor, disabled, showToolbar, customStyle);
            }}
            onError={(error, { willEditorRestart }) => {
              logger.error(error);
            }}
            disabled={disabled}
            onChange={(event, editor) => {
              if (editor && typeof editor.getData === "function") {
                try {
                  let currentData = editor.getData();
                  handleWikiChange(currentData);
                } catch (err) {
                  logger.error("Error in CKEditor onChange:", err);
                }
              }
            }}
            editor={DecoupledEditor}
            data={processedContent}
            config={getEditorConfig()}
          />
        ) : (
          <div className="editor-loading">Loading editor...</div>
        )}
      </div>
    </EditorErrorBoundary>
  );
};

export default TextEditor;
